import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useEventCallback } from 'usehooks-ts';

import type { DownloadAppCommodityModalProps } from '@/features/download/modals/downloadAppCommodityModal';
import type { DuoMatchModalProps } from '@/features/duos/modals/duoMatchModal';
import type { InviteDuoModalProps } from '@/features/invites/modals/inviteDuoModal';
import type { EnableNotificationsModalProps } from '@/features/notifications/modals/enableNotificationsModal';
import type { NotificationPermissionReminderModalProps } from '@/features/notifications/modals/notificationPermissionReminderModal';
import type { FirstWaveModalProps } from '@/features/onboarding/modals/firstWaveModal';
import type { ProfileNonToxicModalProps } from '@/features/profile/modals/profileNonToxicModal';
import type { LowSkipsWarningModalProps } from '@/features/subscription/modals/lowSkipsWarningModal';
import type { PaywallModalProps } from '@/features/subscription/modals/paywallModal';
import type { RevealBalanceLimitModalProps } from '@/features/subscription/modals/revealBalanceLimitModal';
import type { SwipeBalanceLimitModalProps } from '@/features/subscription/modals/swipeBalanceLimitModal';

export type ModalStackItemUnion =
  | FirstWaveModalProps
  | DownloadAppCommodityModalProps
  | DuoMatchModalProps
  | EnableNotificationsModalProps
  | InviteDuoModalProps
  | LowSkipsWarningModalProps
  | NotificationPermissionReminderModalProps
  | PaywallModalProps
  | ProfileNonToxicModalProps
  | RevealBalanceLimitModalProps
  | SwipeBalanceLimitModalProps;

export interface ModalStackState {
  stack: ModalStackItemUnion[];
}

const modalStackAtom = atom<ModalStackState>({
  stack: [],
});

const generateModalId = (type: string): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 11);
  return `${type}-${timestamp.toString()}-${random}`;
};

export const useModalStack = () => {
  return useAtomValue(modalStackAtom);
};

export const useOpenModal = () => {
  const setModalStack = useSetAtom(modalStackAtom);

  return useEventCallback(
    (
      item: Omit<ModalStackItemUnion, 'id' | 'timestamp' | 'isOpen'>,
    ): string => {
      const id = generateModalId(item.type);
      const timestamp = Date.now();

      const newItem = {
        ...item,
        id,
        timestamp,
        isOpen: true,
      } as ModalStackItemUnion;

      setModalStack(prev => ({
        stack: [...prev.stack, newItem],
      }));

      return id;
    },
  );
};

export const useCloseModal = () => {
  const setModalStack = useSetAtom(modalStackAtom);

  return useEventCallback((modalId: string) => {
    setModalStack(prev => ({
      stack: prev.stack.map(item =>
        item.id === modalId ? { ...item, isOpen: false } : item,
      ),
    }));
  });
};

export const useRemoveModal = () => {
  const setModalStack = useSetAtom(modalStackAtom);

  return useEventCallback((modalId: string) => {
    setModalStack(prev => ({
      stack: prev.stack.filter(item => item.id !== modalId),
    }));
  });
};

export const useReopenModal = () => {
  const setModalStack = useSetAtom(modalStackAtom);

  return useEventCallback((modalId: string) => {
    setModalStack(prev => ({
      stack: prev.stack.map(item =>
        item.id === modalId ? { ...item, isOpen: true } : item,
      ),
    }));
  });
};
